import { useState } from "react";

export default function FAQSection() {
  const faqs = [
    {
      q: "What is an expedited passport service?",
      a: "An expedited passport service is a private registered courier that submits your passport application directly to a U.S. federal passport agency on your behalf — bypassing standard mail processing. Unlike applying through the post office, a registered courier like Fast Passport Center has reserved daily submission slots at passport agencies, allowing your application to be processed in as little as 3 business days instead of the standard 4–6 weeks.",
    },
    {
      q: "Is a passport expediting service legitimate?",
      a: "Yes — but only if the company is registered with the U.S. Department of State. The State Dept maintains an official list of registered passport couriers. Fast Passport Center has been on that list for over 20 years. Always verify a company is on the official registry before using their service. Unregistered companies cannot submit applications directly to passport agencies.",
    },
    {
      q: "How fast can I get my passport with an expediting service?",
      a: "Fast Passport Center offers processing in as little as 3 business days from when we receive your documents. Add approximately 1–2 days for your documents to reach us (if mailing) and 1 day for overnight return delivery. Total door-to-door time is typically 5–7 business days for the fastest tier.",
    },
    {
      q: "Why is Fast Passport Center cheaper than FedEx or Staples?",
      a: "FedEx Office and Staples are resellers — they charge you a service fee and then send your application to a registered courier like Fast Passport Center. You pay their fee plus the courier's fee. When you use Fast Passport Center directly, you eliminate the middleman and pay only our direct courier fee — typically 20–80% less than retail passport service counters.",
    },
    {
      q: "Do I still have to pay government fees?",
      a: "Yes. Government fees are paid directly to the U.S. Department of State and are required regardless of which service you use. Fast Passport Center's service fee is separate and covers document review, courier handling, and overnight return delivery. We do not mark up government fees.",
    },
    {
      q: "Can I get an expedited passport without going to a passport agency?",
      a: "Yes — for passport renewals (DS-82), you never need to visit a passport agency or acceptance facility. You mail us your documents and we handle everything. For new passport applications (DS-11) and child passports, an in-person visit to a passport acceptance facility (like a post office or courthouse) is required by law before we can submit your application — but you do not need to go to a regional passport agency yourself. See our <a href='/passport-services/renewal/' style='color:#F6C344;font-weight:600;text-decoration:underline;'>Passport Renewal page</a> and <a href='/passport-services/new-passport/' style='color:#F6C344;font-weight:600;text-decoration:underline;'>New Passport page</a> for details.",
    },
    {
      q: "What if I live far from a passport agency?",
      a: "That's exactly why services like Fast Passport Center exist. Cities like Tampa, Charlotte, Nashville, Louisville, Sacramento, Kansas City, and Bozeman don't have a nearby regional passport agency — the nearest one may be 4–8 hours away. Our courier handles the agency trip for you. You mail us your documents; we handle the rest.",
    },
    {
      q: "How is expedited passport service different from government expedited processing?",
      a: "Government expedited processing (the $60 fee paid to the State Dept) shortens processing to 2–3 weeks — but that doesn't include mailing time, which can add another 2 weeks. A registered courier service like Fast Passport Center physically hand-delivers your application to the agency with reserved daily submission slots, cutting total processing time to as little as 3 business days — far faster than what the $60 government expedite fee alone can achieve.",
    },
    {
      q: "What documents do I need for expedited passport service?",
      a: "Requirements vary by service type. For renewal: your current passport, Form DS-82, one passport photo, and proof of travel. For a new passport: birth certificate, government-issued ID, Form DS-11 (completed at an acceptance facility), one passport photo, and proof of travel. For a child passport: both parents' consent, birth certificate, Form DS-11, and proof of travel. Fast Passport Center provides a personalized document checklist when you start your order. See our <a href='/passport-services/renewal/' style='color:#F6C344;font-weight:600;text-decoration:underline;'>Renewal</a>, <a href='/passport-services/new-passport/' style='color:#F6C344;font-weight:600;text-decoration:underline;'>New Passport</a>, and <a href='/passport-services/child-passport/' style='color:#F6C344;font-weight:600;text-decoration:underline;'>Child Passport</a> pages for full requirements.",
    },
    {
      q: "Can I track my passport application?",
      a: "Yes. Every Fast Passport Center order includes a personal tracking dashboard with real-time status updates from submission through return delivery. You'll also receive email and text updates at each major milestone.",
    },
  ];

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Questions?</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Expedited Passport Service —{" "}
            <span className="text-[#F6C344]">Frequently Asked Questions</span>
          </h2>
          <p className="text-gray-500 text-base">
            Honest answers to the questions we hear most often about expedited passport services.
          </p>
        </div>

        <div className="space-y-2">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden"
            >
              <button
                className="w-full flex items-center justify-between gap-4 p-5 text-left cursor-pointer"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="font-semibold text-[#1A365D] text-sm leading-snug pr-2">{faq.q}</span>
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#1A365D] flex-shrink-0">
                  <i
                    className={`text-white text-sm transition-transform duration-200 ${
                      openIndex === i ? "ri-subtract-line" : "ri-add-line"
                    }`}
                  ></i>
                </div>
              </button>
              {openIndex === i && (
                <div className="px-5 pb-5">
                  <p className="text-gray-500 text-sm leading-relaxed border-t border-gray-200 pt-4" dangerouslySetInnerHTML={{ __html: faq.a }} />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-gray-500 text-sm mb-4">Still have questions? Our team is here to help.</p>
          <a
            href="tel:8772530084"
            className="inline-flex items-center gap-2 bg-[#1A365D] hover:bg-[#0F2240] text-white font-semibold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap"
          >
            <i className="ri-phone-line"></i> Call 877-253-0084
          </a>
        </div>
      </div>
    </section>
  );
}